import {
  type EnumeratedEnumVFrame,
  type FixedStringVFrame,
  type FundCommandSelection,
  type FundCommandSelectionVFrame,
  isFixedStringVFrame,
  type PartitionCommandSelectionMFrame,
  type StringVFrame,
} from '@festina/unitlink-general-types/unitlink'
import { useFetchFunds } from '@festina-life/api'
import { notEmpty, type Optional, typeUtils } from '@festina-life/core'
import { Button } from '@festina-life/ui/actions'
import { useTranslate } from '@festina-life/ui/hooks'
import { Modal } from '@festina-life/ui/structure'
import { useId, useState } from 'react'

import { useHook } from '../../../lenteHooks'
import { LifeForm } from '../../forms/LifeForm'
import { isFixedVFrame } from '../../forms/yup/validation/types'
import { LifeInput } from '../../inputs/LifeInput'

const validateKey = typeUtils.createValidator<FundCommandSelection>()

type Props = {
  fundCommandSelection: FundCommandSelection
  existingFundIsinKeys: string[]
  onClose: () => void
  onSubmit: (updatedFund: FundCommandSelection) => void
  mFrame: Optional<PartitionCommandSelectionMFrame>
}

export const EditFundCommandSelectionModal = ({
  fundCommandSelection,
  existingFundIsinKeys,
  onClose,
  onSubmit,
  mFrame,
}: Props) => {
  const translate = useTranslate()
  const [isinKey, setIsinKey] = useState<Optional<string>>(
    fundCommandSelection?.isinKey,
  )

  const tenantId = useHook('routing.tenant.useTenantParam')()
  const { data: funds } = useFetchFunds({ tenantId })
  const allFunds = funds?.fundItems?.filter(notEmpty) ?? []

  const updateMFrame = mFrame?.updateFundCommandSelections?.find(
    (updateFundCommandSelectionMFrame) => {
      const isinKeyFrame = updateFundCommandSelectionMFrame?.isinKey
      return isFixedStringVFrame(isinKeyFrame) && isinKeyFrame.value === isinKey
    },
  )

  const isUpdate = !!fundCommandSelection.isinKey

  // If we are updating an existing fund, we only want to allow editing of the fraction
  const updateFrame: FundCommandSelectionVFrame = {
    _type: 'FundCommandSelectionFrame',
    isinKey: {
      _type: 'FixedEnumVFrame',
      value: isinKey,
    } satisfies FixedStringVFrame as StringVFrame,
    fraction: updateMFrame?.fraction,
  }

  // If we are adding a new fund, we want to use the addFundCommandSelections mFrames to populate the frame
  const addIsinKeyValues =
    mFrame?.addFundCommandSelections
      ?.map((addFundCommandSelectionMFrame) => {
        const isinKeyFrame = addFundCommandSelectionMFrame?.isinKey
        return isFixedStringVFrame(isinKeyFrame) ? isinKeyFrame.value : null
      })
      .filter(notEmpty) ?? []

  const addIsinKeyFrame: EnumeratedEnumVFrame = {
    _type: 'EnumeratedEnumVFrame',
    defaultValue: isinKey,
    values: addIsinKeyValues.filter(
      (isinKey) => !existingFundIsinKeys.includes(isinKey), // can't add a fund that has already been added
    ),
  }

  const addMFrame = mFrame?.addFundCommandSelections?.find(
    (addFundCommandSelectionMFrame) => {
      const isinKeyFrame = addFundCommandSelectionMFrame?.isinKey
      return isFixedVFrame(isinKeyFrame) && isinKeyFrame.value === isinKey
    },
  )

  const addFrame: FundCommandSelectionVFrame = {
    _type: 'FundCommandSelectionFrame',
    isinKey: addIsinKeyFrame,
    fraction: addMFrame?.fraction,
  }

  const frame = isUpdate ? updateFrame : addFrame

  const initialValue = fundCommandSelection ?? {
    _type: 'PartitionCommandSelection',
    isinKey,
  }

  const formId = useId()
  return (
    <Modal onClose={onClose} isOpen>
      <Modal.Title title={translate('editFundCommandSelectionModal.fund')} />
      <Modal.Body>
        <LifeForm
          formId={formId}
          initialValues={initialValue || { _type: 'FundCommandSelection' }}
          onSubmit={onSubmit}
          frame={frame}
        >
          <LifeInput
            fallbackInputType="string"
            name={validateKey('isinKey')}
            label={translate('editFundCommandSelectionModal.isinKey')}
            onBlur={setIsinKey}
            disabled={isUpdate}
            renderFn={(isinKey) =>
              allFunds.find((fund) => fund.isinKey === isinKey)?.name || isinKey
            }
          />
          <LifeInput
            fallbackInputType="percentage"
            name={validateKey('fraction')}
            label={translate('editFundCommandSelectionModal.fraction')}
          />
        </LifeForm>
        <Modal.Footer>
          <Button variant="discreet" onClick={onClose}>
            {translate('cancel')}
          </Button>
          <Button type="submit" form={formId}>
            {translate('save')}
          </Button>
        </Modal.Footer>
      </Modal.Body>
    </Modal>
  )
}
